import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DesignCataloguePreview } from '../../components/design/DesignCataloguePreview';
import { DesignTemplateGallery } from '../../components/design/DesignTemplateGallery';
import { PageHeader } from '../../components/ui/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalPath } from '../../contexts/PortalContext';
import {
  formatDesignEditorsLabel,
  useDesignCataloguePresence,
} from '../../hooks/useDesignCataloguePresence';
import { useDesignCatalogueRealtime } from '../../hooks/useDesignCatalogueRealtime';
import {
  duplicateDesignCatalogue,
  emptyDesignCatalogueItem,
  fetchDesignCatalogueProject,
  saveDesignCatalogue,
  uploadDesignCatalogueImage,
  type DesignCatalogueDraftItem,
  type DesignCatalogueStatus,
  type DesignCoverBgMode,
} from '../../lib/designCatalogues';
import { exportDesignCataloguePdf } from '../../lib/designCataloguePdf';
import { extractDesignCoverPalette } from '../../lib/designImagePalette';
import {
  DESIGN_CATALOGUE_TEMPLATES,
  defaultLayoutRows,
  designTemplateById,
  maxLayoutColumns,
  maxLayoutRows,
  resolveDesignLayoutGrid,
  type DesignCatalogueTemplateId,
} from '../../lib/designTemplates';

export function DesignCatalogueEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const base = usePortalPath();
  const { authUser, profile } = useAuth();
  const previewRef = useRef<HTMLDivElement>(null);

  const [title, setTitle] = useState('Untitled catalogue');
  const [templateId, setTemplateId] = useState<DesignCatalogueTemplateId>(
    DESIGN_CATALOGUE_TEMPLATES[0]!.id,
  );
  const [status, setStatus] = useState<DesignCatalogueStatus>('draft');
  const [coverTitle, setCoverTitle] = useState('');
  const [coverSubtitle, setCoverSubtitle] = useState(
    DESIGN_CATALOGUE_TEMPLATES[0]?.defaultSubtitle ?? 'Special Price',
  );
  const [coverNotes, setCoverNotes] = useState('');
  const [coverBgMode, setCoverBgMode] = useState<DesignCoverBgMode>('theme');
  const [coverBgColor, setCoverBgColor] = useState('#9a3412');
  const [coverBgColorTo, setCoverBgColorTo] = useState('#fbbf24');
  const [coverBgImagePath, setCoverBgImagePath] = useState<string | null>(null);
  const [coverBgImageUrl, setCoverBgImageUrl] = useState<string | null>(null);
  const [coverBgUploading, setCoverBgUploading] = useState(false);
  const [layoutColumns, setLayoutColumns] = useState<number>(
    DESIGN_CATALOGUE_TEMPLATES[0]!.columns,
  );
  const [layoutRows, setLayoutRows] = useState<number>(
    defaultLayoutRows(DESIGN_CATALOGUE_TEMPLATES[0]!),
  );
  const [items, setItems] = useState<DesignCatalogueDraftItem[]>([emptyDesignCatalogueItem(0)]);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(isNew ? null : id!);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [conflictToast, setConflictToast] = useState<string | null>(null);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  const actorEmail = authUser?.email ?? profile?.email ?? null;
  const actorId = authUser?.id ?? null;

  const otherEditors = useDesignCataloguePresence(projectId);
  const editorsLabel = formatDesignEditorsLabel(otherEditors);
  const expectedUpdatedAtRef = useRef(expectedUpdatedAt);
  expectedUpdatedAtRef.current = expectedUpdatedAt;
  const savingRef = useRef(saving);
  savingRef.current = saving;

  const activeTemplate = designTemplateById(templateId);
  const gridLockedToOneCol =
    activeTemplate.cardLayout === 'photo-left' || activeTemplate.cardLayout === 'ticket';
  const gridMaxColumns = maxLayoutColumns(activeTemplate);
  const gridMaxRows = maxLayoutRows(activeTemplate);
  const resolvedGrid = resolveDesignLayoutGrid(activeTemplate, {
    columns: layoutColumns,
    rows: layoutRows,
  });

  const load = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    setError(null);
    try {
      const { project, items: rows } = await fetchDesignCatalogueProject(id);
      const tmpl = designTemplateById(project.template_id);
      setProjectId(project.id);
      setTitle(project.title);
      setTemplateId(project.template_id as DesignCatalogueTemplateId);
      setStatus(project.status === 'archived' ? 'draft' : project.status);
      setCoverTitle(project.cover_title);
      setCoverSubtitle(project.cover_subtitle);
      setCoverNotes(project.cover_notes);
      setCoverBgMode(project.cover_bg_mode ?? 'theme');
      setCoverBgColor(project.cover_bg_color || tmpl.swatchFrom);
      setCoverBgColorTo(project.cover_bg_color_to || tmpl.swatchTo);
      setCoverBgImagePath(project.cover_bg_image_path);
      setCoverBgImageUrl(project.cover_bg_image_url ?? null);
      if (project.template_id === 'promo_grid_compact' && project.layout_columns == null) {
        setLayoutColumns(3);
        setLayoutRows(project.layout_rows ?? 2);
      } else {
        setLayoutColumns(project.layout_columns ?? tmpl.columns);
        setLayoutRows(project.layout_rows ?? defaultLayoutRows(tmpl));
      }
      setExpectedUpdatedAt(project.updated_at);
      setItems(rows.length > 0 ? rows : [emptyDesignCatalogueItem(0)]);

      // Older image-mode projects may lack saved palette — extract once from cover image.
      if (
        (project.cover_bg_mode ?? 'theme') === 'image' &&
        project.cover_bg_image_url &&
        !String(project.cover_bg_color ?? '').trim()
      ) {
        void extractDesignCoverPalette(project.cover_bg_image_url, {
          from: tmpl.swatchFrom,
          to: tmpl.swatchTo,
        }).then((palette) => {
          setCoverBgColor(palette.from);
          setCoverBgColorTo(palette.to);
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load catalogue.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    void load();
  }, [load]);

  // Soft signal only — hard reload would wipe unsaved local edits.
  useDesignCatalogueRealtime({
    enabled: Boolean(projectId) && !isNew,
    projectId: projectId ?? undefined,
    onChange: () => {
      if (!projectId || savingRef.current) return;
      void fetchDesignCatalogueProject(projectId)
        .then(({ project }) => {
          const local = expectedUpdatedAtRef.current;
          if (local && project.updated_at !== local) {
            setConflictToast(
              'This catalogue was updated elsewhere. Reload or save carefully — your local draft may be stale.',
            );
          }
        })
        .catch(() => {
          /* ignore background check failures */
        });
    },
  });

  const previewModel = useMemo(
    () => ({
      title,
      templateId,
      coverTitle,
      coverSubtitle,
      coverNotes,
      items,
      layoutColumns,
      layoutRows,
      coverBgMode,
      coverBgColor,
      coverBgColorTo,
      coverBgImageUrl,
    }),
    [
      title,
      templateId,
      coverTitle,
      coverSubtitle,
      coverNotes,
      items,
      layoutColumns,
      layoutRows,
      coverBgMode,
      coverBgColor,
      coverBgColorTo,
      coverBgImageUrl,
    ],
  );

  function selectTemplate(nextId: DesignCatalogueTemplateId) {
    setTemplateId(nextId);
    const tmpl = designTemplateById(nextId);
    if (tmpl.defaultSubtitle) setCoverSubtitle(tmpl.defaultSubtitle);
    setLayoutColumns(tmpl.columns);
    setLayoutRows(defaultLayoutRows(tmpl));
  }

  function patchItem(index: number, patch: Partial<DesignCatalogueDraftItem>) {
    setItems((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function addItem() {
    setItems((prev) => [...prev, emptyDesignCatalogueItem(prev.length)]);
  }

  function removeItem(index: number) {
    setItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [emptyDesignCatalogueItem(0)];
    });
  }

  function moveItem(index: number, dir: -1 | 1) {
    setItems((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      const tmp = next[index]!;
      next[index] = next[j]!;
      next[j] = tmp;
      return next;
    });
  }

  async function ensureProjectIdForUpload(): Promise<string> {
    if (projectId) return projectId;
    const result = await saveDesignCatalogue({
      title: title.trim() || 'Untitled catalogue',
      template_id: templateId,
      status: 'draft',
      cover_title: coverTitle,
      cover_subtitle: coverSubtitle,
      cover_notes: coverNotes,
      layout_columns: layoutColumns,
      layout_rows: layoutRows,
      cover_bg_mode: coverBgMode,
      cover_bg_color: coverBgColor,
      cover_bg_color_to: coverBgColorTo,
      cover_bg_image_path: coverBgImagePath,
      items: [],
      actorEmail,
      actorId,
    });
    setProjectId(result.project.id);
    setExpectedUpdatedAt(result.project.updated_at);
    navigate(`${base}/catalogues/${result.project.id}`, { replace: true });
    return result.project.id;
  }

  async function handleImagePick(index: number, file: File | null) {
    if (!file) return;
    setUploadingIndex(index);
    setError(null);
    try {
      const pid = await ensureProjectIdForUpload();
      const path = await uploadDesignCatalogueImage(pid, file);
      const localUrl = URL.createObjectURL(file);
      patchItem(index, { image_path: path, image_url: localUrl });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Image upload failed.');
    } finally {
      setUploadingIndex(null);
    }
  }

  async function handleSave(nextStatus?: DesignCatalogueStatus) {
    const statusToSave = nextStatus ?? status;
    if (nextStatus) setStatus(nextStatus);
    setSaving(true);
    setError(null);
    setMessage(null);
    setConflictToast(null);
    try {
      const result = await saveDesignCatalogue({
        id: projectId ?? undefined,
        expectedUpdatedAt,
        title,
        template_id: templateId,
        status: statusToSave,
        cover_title: coverTitle,
        cover_subtitle: coverSubtitle,
        cover_notes: coverNotes,
        layout_columns: layoutColumns,
        layout_rows: layoutRows,
        cover_bg_mode: coverBgMode,
        cover_bg_color: coverBgColor,
        cover_bg_color_to: coverBgColorTo,
        cover_bg_image_path: coverBgImagePath,
        items,
        actorEmail,
        actorId,
      });
      setProjectId(result.project.id);
      setExpectedUpdatedAt(result.project.updated_at);
      setStatus(result.project.status === 'archived' ? 'draft' : result.project.status);
      if (result.project.layout_columns != null) setLayoutColumns(result.project.layout_columns);
      if (result.project.layout_rows != null) setLayoutRows(result.project.layout_rows);
      setCoverBgMode(result.project.cover_bg_mode);
      setCoverBgColor(result.project.cover_bg_color || coverBgColor);
      setCoverBgColorTo(result.project.cover_bg_color_to || coverBgColorTo);
      setCoverBgImagePath(result.project.cover_bg_image_path);
      if (result.project.cover_bg_image_url !== undefined) {
        setCoverBgImageUrl(result.project.cover_bg_image_url ?? null);
      }
      setItems(result.items.length > 0 ? result.items : items);
      if (result.conflict && result.conflictMessage) {
        setConflictToast(result.conflictMessage);
      } else {
        setMessage(
          statusToSave === 'published'
            ? 'Published.'
            : statusToSave === 'draft' && nextStatus
              ? 'Saved as draft.'
              : 'Saved.',
        );
      }
      if (isNew || id !== result.project.id) {
        navigate(`${base}/catalogues/${result.project.id}`, { replace: true });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDuplicate() {
    if (!projectId) return;
    setDuplicating(true);
    setError(null);
    try {
      const copy = await duplicateDesignCatalogue(projectId, {
        email: actorEmail,
        id: actorId,
      });
      navigate(`${base}/catalogues/${copy.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Duplicate failed.');
    } finally {
      setDuplicating(false);
    }
  }

  async function applyPaletteFromImage(
    imageUrl: string,
    fallbackFrom = activeTemplate.swatchFrom,
    fallbackTo = activeTemplate.swatchTo,
  ) {
    const palette = await extractDesignCoverPalette(imageUrl, {
      from: fallbackFrom,
      to: fallbackTo,
    });
    setCoverBgColor(palette.from);
    setCoverBgColorTo(palette.to);
  }

  async function handleCoverBgImage(file: File | null) {
    if (!file) return;
    setCoverBgUploading(true);
    setError(null);
    try {
      const pid = await ensureProjectIdForUpload();
      const path = await uploadDesignCatalogueImage(pid, file);
      const localUrl = URL.createObjectURL(file);
      setCoverBgImagePath(path);
      setCoverBgImageUrl(localUrl);
      setCoverBgMode('image');
      await applyPaletteFromImage(localUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Cover background upload failed.');
    } finally {
      setCoverBgUploading(false);
    }
  }

  async function handleExport() {
    if (!previewRef.current) return;
    setExporting(true);
    setError(null);
    try {
      await exportDesignCataloguePdf(
        previewRef.current,
        `${title.trim() || 'catalogue'}.pdf`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'PDF export failed.');
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="design-catalogue-editor">
        <p className="page-lead">Loading catalogue…</p>
      </div>
    );
  }

  return (
    <div className="design-catalogue-editor">
      <PageHeader
        title={isNew ? 'New catalogue' : 'Edit catalogue'}
        actions={
          <div className="design-catalogue-editor__actions">
            <Link className="btn-secondary btn-sm" to={`${base}/catalogues`}>
              Back to list
            </Link>
            {projectId ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={duplicating}
                onClick={() => void handleDuplicate()}
              >
                {duplicating ? 'Copying…' : 'Duplicate'}
              </button>
            ) : null}
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={exporting}
              onClick={() => void handleExport()}
            >
              {exporting ? 'Exporting…' : 'Export PDF'}
            </button>
            {status === 'published' ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={saving}
                onClick={() => void handleSave('draft')}
              >
                Unpublish
              </button>
            ) : (
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={saving}
                onClick={() => void handleSave('published')}
              >
                Publish
              </button>
            )}
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={saving}
              onClick={() => void handleSave()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      />

      {editorsLabel ? (
        <div className="design-cat-presence" role="status">
          <span className="design-cat-presence__dot" aria-hidden />
          <span>{editorsLabel}</span>
        </div>
      ) : null}

      {conflictToast ? (
        <div className="design-cat-conflict" role="status">
          <strong>Overwrite notice</strong>
          <span>{conflictToast}</span>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setConflictToast(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {error ? <p className="form-error">{error}</p> : null}
      {message ? <p className="form-success">{message}</p> : null}

      <div className="design-catalogue-editor__layout">
        <section className="design-catalogue-editor__form card">
          <h2 className="design-catalogue-editor__section-title">Catalogue</h2>
          <input
            className="field-input"
            value={title}
            placeholder="Catalogue title"
            aria-label="Catalogue title"
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="design-catalogue-editor__status-row">
            <span className={`design-cat-status design-cat-status--${status}`}>{status}</span>
            <span className="design-catalogue-editor__status-hint">
              Use Publish when ready to share with the team.
            </span>
          </div>

          <h2 className="design-catalogue-editor__section-title">Template</h2>
          <DesignTemplateGallery value={templateId} onChange={selectTemplate} />

          <h2 className="design-catalogue-editor__section-title">Grid</h2>
          <div className="design-catalogue-editor__grid-controls">
            <label className="design-catalogue-editor__grid-field">
              <span>Columns</span>
              <select
                className="field-input"
                value={gridLockedToOneCol ? 1 : Math.min(layoutColumns, gridMaxColumns)}
                disabled={gridLockedToOneCol}
                aria-label="Columns per page"
                onChange={(e) => setLayoutColumns(Number(e.target.value))}
              >
                {Array.from({ length: gridMaxColumns }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label className="design-catalogue-editor__grid-field">
              <span>Rows</span>
              <select
                className="field-input"
                value={Math.min(layoutRows, gridMaxRows)}
                aria-label="Rows per page"
                onChange={(e) => setLayoutRows(Number(e.target.value))}
              >
                {Array.from({ length: gridMaxRows }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="design-catalogue-editor__grid-hint">
            {resolvedGrid.columns}×{resolvedGrid.rows} · {resolvedGrid.itemsPerPage} products / page
            (extra products go to the next page)
            {gridLockedToOneCol ? ' · this template is single-column' : ''}
            · max {gridMaxColumns}×{gridMaxRows} for this template
          </p>

          <h2 className="design-catalogue-editor__section-title">Cover</h2>
          <div className="design-catalogue-editor__row2">
            <input
              className="field-input"
              value={coverTitle}
              placeholder="Cover title (optional)"
              aria-label="Cover title"
              onChange={(e) => setCoverTitle(e.target.value)}
            />
            <input
              className="field-input"
              value={coverSubtitle}
              placeholder="Subtitle"
              aria-label="Cover subtitle"
              onChange={(e) => setCoverSubtitle(e.target.value)}
            />
          </div>
          <input
            className="field-input design-catalogue-editor__spaced"
            value={coverNotes}
            placeholder="Cover notes (optional)"
            aria-label="Cover notes"
            onChange={(e) => setCoverNotes(e.target.value)}
          />

          <h2 className="design-catalogue-editor__section-title">Colours</h2>
          <div className="design-catalogue-editor__cover-bg">
            <div className="design-catalogue-editor__cover-bg-mode" role="group" aria-label="Colour mode">
              {(
                [
                  ['theme', 'Template'],
                  ['color', 'Colour'],
                  ['image', 'Image'],
                ] as const
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  className={coverBgMode === mode ? 'is-active' : undefined}
                  onClick={() => {
                    setCoverBgMode(mode);
                    if (mode === 'color') {
                      setCoverBgColor(activeTemplate.swatchFrom);
                      setCoverBgColorTo(activeTemplate.swatchTo);
                    }
                    if (mode === 'image' && coverBgImageUrl) {
                      void applyPaletteFromImage(coverBgImageUrl);
                    }
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {coverBgMode === 'theme' ? (
              <p className="design-catalogue-editor__cover-bg-hint">
                Uses the selected template’s default colours for cover and products.
              </p>
            ) : null}

            {coverBgMode === 'color' ? (
              <>
                <div className="design-catalogue-editor__cover-bg-colors">
                  <label className="design-catalogue-editor__cover-bg-color">
                    <span>From (top / accent)</span>
                    <input
                      type="color"
                      value={coverBgColor}
                      onChange={(e) => setCoverBgColor(e.target.value)}
                    />
                  </label>
                  <label className="design-catalogue-editor__cover-bg-color">
                    <span>To (panel / products)</span>
                    <input
                      type="color"
                      value={coverBgColorTo}
                      onChange={(e) => setCoverBgColorTo(e.target.value)}
                    />
                  </label>
                </div>
                <p className="design-catalogue-editor__cover-bg-hint">
                  From = top / accent. To = lower panel on two-part covers, and tints product cards /
                  headers on every page.
                </p>
              </>
            ) : null}

            {coverBgMode === 'image' ? (
              <>
                <div className="design-catalogue-editor__cover-bg-image">
                  {coverBgImageUrl ? (
                    <img className="design-catalogue-editor__cover-bg-thumb" src={coverBgImageUrl} alt="" />
                  ) : null}
                  <label className="btn-secondary btn-sm">
                    {coverBgUploading ? 'Uploading…' : coverBgImageUrl ? 'Replace image' : 'Upload image'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      disabled={coverBgUploading}
                      onChange={(e) => void handleCoverBgImage(e.target.files?.[0] ?? null)}
                    />
                  </label>
                  {coverBgImagePath ? (
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() => {
                        setCoverBgImagePath(null);
                        setCoverBgImageUrl(null);
                        setCoverBgMode('theme');
                      }}
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
                <p className="design-catalogue-editor__cover-bg-hint">
                  Recommended size: <strong>2480 × 3508 px</strong> (A4 @ 300 dpi) or{' '}
                  <strong>1240 × 1754 px</strong> (A4 @ 150 dpi). JPG / PNG / WebP, cover-fit, under
                  8 MB. Colours are sampled from the image to tint product pages and lower panels;
                  Save to keep them.
                </p>
                {coverBgImageUrl ? (
                  <div className="design-catalogue-editor__cover-bg-colors">
                    <label className="design-catalogue-editor__cover-bg-color">
                      <span>From (sampled)</span>
                      <input
                        type="color"
                        value={coverBgColor}
                        onChange={(e) => setCoverBgColor(e.target.value)}
                      />
                    </label>
                    <label className="design-catalogue-editor__cover-bg-color">
                      <span>To (sampled)</span>
                      <input
                        type="color"
                        value={coverBgColorTo}
                        onChange={(e) => setCoverBgColorTo(e.target.value)}
                      />
                    </label>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </section>

        <section className="design-catalogue-editor__preview-pane">
          <h2 className="design-catalogue-editor__section-title">Live preview</h2>
          <div className="design-catalogue-editor__preview-scroll">
            <DesignCataloguePreview model={previewModel} captureRef={previewRef} />
          </div>
        </section>
      </div>

      <section className="design-catalogue-products card">
        <div className="design-catalogue-editor__items-head">
          <h2 className="design-catalogue-editor__section-title">Products</h2>
          <button type="button" className="btn-secondary btn-sm" onClick={addItem}>
            Add product
          </button>
        </div>

        <div className="design-catalogue-products__table-wrap">
          <table className="design-catalogue-products__table">
            <thead>
              <tr>
                <th className="design-catalogue-products__col-num">#</th>
                <th className="design-catalogue-products__col-photo">Photo</th>
                <th>Category</th>
                <th>Name</th>
                <th>Code</th>
                <th>Packing</th>
                <th>Price</th>
                <th>Promo</th>
                <th>Badge</th>
                <th>Notes</th>
                <th className="design-catalogue-products__col-actions" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index}>
                  <td className="design-catalogue-products__col-num">{index + 1}</td>
                  <td className="design-catalogue-products__col-photo">
                    <label className="design-catalogue-products__photo">
                      {item.image_url ? (
                        <img src={item.image_url} alt="" />
                      ) : (
                        <span>+</span>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        disabled={uploadingIndex === index}
                        onChange={(e) => void handleImagePick(index, e.target.files?.[0] ?? null)}
                      />
                      {uploadingIndex === index ? (
                        <span className="design-catalogue-products__photo-busy">…</span>
                      ) : null}
                    </label>
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={item.category}
                      placeholder="Category"
                      onChange={(e) => patchItem(index, { category: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={item.name}
                      placeholder="Product name"
                      onChange={(e) => patchItem(index, { name: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={item.item_code}
                      placeholder="Code"
                      onChange={(e) => patchItem(index, { item_code: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={item.packing}
                      placeholder="Packing"
                      onChange={(e) => patchItem(index, { packing: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={item.price}
                      placeholder="Price (optional)"
                      onChange={(e) => patchItem(index, { price: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={item.promo_price}
                      placeholder="Promo (optional)"
                      onChange={(e) => patchItem(index, { promo_price: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={item.badge}
                      placeholder="Badge"
                      onChange={(e) => patchItem(index, { badge: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={item.notes}
                      placeholder="Notes"
                      onChange={(e) => patchItem(index, { notes: e.target.value })}
                    />
                  </td>
                  <td className="design-catalogue-products__col-actions">
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      title="Move up"
                      onClick={() => moveItem(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      title="Move down"
                      onClick={() => moveItem(index, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      title="Remove"
                      onClick={() => removeItem(index)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
